import { useState, useRef, useEffect } from 'react';
import { Button, message, Card, Progress, Tag, Tooltip, Modal, Form, Input } from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  BorderOutlined,
  RadiusSettingOutlined,
  LineOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';

const ModernROIDrawer = ({ onSaveClick, projectName }) => {
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);
  const [allShapes, setAllShapes] = useState({});
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [currentCircle, setCurrentCircle] = useState(null);
  const [currentLine, setCurrentLine] = useState(null);
  const [tempPoint, setTempPoint] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roiCode, setRoiCode] = useState(null);
  const [drawMode, setDrawMode] = useState('polygon'); // 'polygon', 'circle', 'line'
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const username = user?.username;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [shapeMeta, setShapeMeta] = useState({
    roiName: '',
    roiType: 'object'
  });
  const [newShapeData, setNewShapeData] = useState(null);
  
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f43f5e', '#a855f7', '#14b8a6', '#6366f1'];

  useEffect(() => {
    fetchImages();
  }, [projectName]);

  const fetchImages = async () => {
    setLoadingImages(true);
    try {
        const response = await axios.post('/fetch-image', { username });

        if (response?.data?.success && response?.data?.roi) {
        const { image, imageroi, code } = response.data.roi;

        setImages(image);
        setRoiCode(code);

        // Build shapes map from backend
        const shapesMap = {};

        image.forEach(img => {
            const roiForImage = imageroi?.find(r => r.imageId === img._id);

            shapesMap[img._id] = roiForImage
            ? roiForImage.shapes?.map(s => ({
                type: s.type || 'polygon',
                points: s.points,
                center: s.center,
                radius: s.radius,
                start: s.start,
                end: s.end,
                color: s.color || '#3b82f6',
                meta: s.meta || {}
                })) || roiForImage.polygons?.map(p => ({
                  type: 'polygon',
                  points: p.points,
                  color: p.color || '#3b82f6',
                  meta: p.meta || {}
                })) || []
            : [];
        });

        setAllShapes(shapesMap);
        } else {
        message.warning('No images found');
        }
    } catch (err) {
        console.error('Error fetching images:', err);
        message.error(err.response?.data?.msg || 'Failed to fetch images');
    } finally {
        setLoadingImages(false);
    }
  };

  const currentImage = images[currentImageIndex];
  const currentImageShapes = currentImage ? (allShapes[currentImage._id] || []) : [];

  useEffect(() => {
    if (currentImage) {
      drawShapes();
    }
  }, [currentImage, currentImageShapes, currentPolygon, currentCircle, currentLine, tempPoint]);

  const clampPoints = (points, width, height) =>
    points.map(p => ({
        x: Math.min(Math.max(p.x, 0), width),
        y: Math.min(Math.max(p.y, 0), height)
    }));

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
    };
  };

  const drawShapes = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const image = imageRef.current;

    if (!image || !ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Draw saved shapes
    currentImageShapes.forEach((shape, idx) => {
      const label = shape.meta?.roiName || `ROI ${idx + 1}`;
      
      if (shape.type === 'polygon') {
        drawPolygon(ctx, shape.points, shape.color, label, false);
      } else if (shape.type === 'circle') {
        drawCircle(ctx, shape.center, shape.radius, shape.color, label, false);
      } else if (shape.type === 'line') {
        drawLine(ctx, shape.start, shape.end, shape.color, label, false);
      }
    });

    // Draw current shape being drawn
    const colorIndex = currentImageShapes.length;
    const currentColor = colors[colorIndex % colors.length];

    if (drawMode === 'polygon' && currentPolygon.length > 0) {
      drawPolygon(ctx, [...currentPolygon, tempPoint].filter(p => p), currentColor, null, true);
    } else if (drawMode === 'circle' && currentCircle) {
      drawCircle(ctx, currentCircle.center, currentCircle.radius, currentColor, null, true);
    } else if (drawMode === 'line' && currentLine) {
      drawLine(ctx, currentLine.start, currentLine.end || tempPoint, currentColor, null, true);
    }
  };

  const drawPolygon = (ctx, points, color, label = null, isDrawing = false) => {
    if (!points || points.length < 2) return;

    const safePoints = clampPoints(points, ctx.canvas.width, ctx.canvas.height);

    // Fill
    ctx.fillStyle = color + (isDrawing ? '20' : '30');
    ctx.beginPath();
    ctx.moveTo(safePoints[0].x, safePoints[0].y);
    safePoints.forEach(point => ctx.lineTo(point.x, point.y));
    ctx.closePath();
    ctx.fill();

    // Border
    ctx.strokeStyle = color;
    ctx.lineWidth = isDrawing ? 2 : 3;
    ctx.setLineDash(isDrawing ? [5, 5] : []);
    ctx.beginPath();
    ctx.moveTo(safePoints[0].x, safePoints[0].y);
    safePoints.forEach(point => ctx.lineTo(point.x, point.y));
    if (!isDrawing) ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Vertices
    safePoints.forEach((point, index) => {
        ctx.fillStyle = index === 0 ? '#10b981' : color;
        ctx.beginPath();
        ctx.arc(point.x, point.y, index === 0 ? 8 : 6, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
    });

    // Label
    if (label && !isDrawing) {
        const center = getPolygonCenter(safePoints);
        drawLabel(ctx, center, label, color);
    }
  };

  const drawCircle = (ctx, center, radius, color, label = null, isDrawing = false) => {
    if (!center || !radius) return;

    // Clamp center and radius
    const safeCenter = {
      x: Math.min(Math.max(center.x, 0), ctx.canvas.width),
      y: Math.min(Math.max(center.y, 0), ctx.canvas.height)
    };
    const safeRadius = Math.min(radius, ctx.canvas.width / 2, ctx.canvas.height / 2);

    // Fill
    ctx.fillStyle = color + (isDrawing ? '20' : '30');
    ctx.beginPath();
    ctx.arc(safeCenter.x, safeCenter.y, safeRadius, 0, 2 * Math.PI);
    ctx.fill();

    // Border
    ctx.strokeStyle = color;
    ctx.lineWidth = isDrawing ? 2 : 3;
    ctx.setLineDash(isDrawing ? [5, 5] : []);
    ctx.beginPath();
    ctx.arc(safeCenter.x, safeCenter.y, safeRadius, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.setLineDash([]);

    // Center point
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(safeCenter.x, safeCenter.y, 8, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label
    if (label && !isDrawing) {
        drawLabel(ctx, safeCenter, label, color);
    }
  };

  const drawLine = (ctx, start, end, color, label = null, isDrawing = false) => {
    if (!start || !end) return;

    const safeStart = {
      x: Math.min(Math.max(start.x, 0), ctx.canvas.width),
      y: Math.min(Math.max(start.y, 0), ctx.canvas.height)
    };
    const safeEnd = {
      x: Math.min(Math.max(end.x, 0), ctx.canvas.width),
      y: Math.min(Math.max(end.y, 0), ctx.canvas.height)
    };

    // Line
    ctx.strokeStyle = color;
    ctx.lineWidth = isDrawing ? 4 : 6;
    ctx.setLineDash(isDrawing ? [5, 5] : []);
    ctx.beginPath();
    ctx.moveTo(safeStart.x, safeStart.y);
    ctx.lineTo(safeEnd.x, safeEnd.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Start point
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(safeStart.x, safeStart.y, 8, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // End point
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(safeEnd.x, safeEnd.y, 8, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label
    if (label && !isDrawing) {
        const midPoint = {
          x: (safeStart.x + safeEnd.x) / 2,
          y: (safeStart.y + safeEnd.y) / 2
        };
        drawLabel(ctx, midPoint, label, color);
    }
  };

  const drawLabel = (ctx, center, label, color) => {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(center.x, center.y, 20, 0, 2 * Math.PI);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.fillStyle = color;
    ctx.font = 'bold 16px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, center.x, center.y);
  };

  const getPolygonCenter = (points) => {
    const sum = points.reduce((acc, point) => ({
      x: acc.x + point.x,
      y: acc.y + point.y
    }), { x: 0, y: 0 });
    
    return {
      x: sum.x / points.length,
      y: sum.y / points.length
    };
  };

  const handleCanvasClick = (e) => {
    if (!currentImage) return;

    const { x, y } = getCanvasPoint(e);

    if (drawMode === 'polygon') {
      if (currentPolygon.length > 2 && isNearStartPoint(currentPolygon[0], { x, y })) {
        // Complete polygon
        setNewShapeData({
          type: 'polygon',
          points: currentPolygon
        });
        setIsModalVisible(true);
        setCurrentPolygon([]);
        setTempPoint(null);
        setIsDrawing(false);
      } else {
        setCurrentPolygon([...currentPolygon, { x, y }]);
        setIsDrawing(true);
      }
    } else if (drawMode === 'circle') {
      if (!currentCircle) {
        // First click - set center
        setCurrentCircle({ center: { x, y }, radius: 0 });
        setIsDrawing(true);
      } else {
        // Second click - complete circle
        const dx = x - currentCircle.center.x;
        const dy = y - currentCircle.center.y;
        const radius = Math.sqrt(dx * dx + dy * dy);
        
        setNewShapeData({
          type: 'circle',
          center: currentCircle.center,
          radius
        });
        setIsModalVisible(true);
        setCurrentCircle(null);
        setTempPoint(null);
        setIsDrawing(false);
      }
    } else if (drawMode === 'line') {
      if (!currentLine) {
        // First click - set start point
        setCurrentLine({ start: { x, y }, end: null });
        setIsDrawing(true);
      } else {
        // Second click - complete line
        setNewShapeData({
          type: 'line',
          start: currentLine.start,
          end: { x, y }
        });
        setIsModalVisible(true);
        setCurrentLine(null);
        setTempPoint(null);
        setIsDrawing(false);
      }
    }
  };

  const handleModalOk = () => {
    const colorIndex = currentImageShapes.length;

    const newShape = {
      ...newShapeData,
      color: colors[colorIndex % colors.length],
      meta: shapeMeta
    };

    setAllShapes(prev => ({
        ...prev,
        [currentImage._id]: [...(prev[currentImage._id] || []), newShape]
    }));

    setIsModalVisible(false);
    setShapeMeta({ roiName: '', roiType: 'object' });
    setNewShapeData(null);
  };

  const handleCanvasMove = (e) => {
    if (!isDrawing) return;

    const { x, y } = getCanvasPoint(e);

    if (drawMode === 'polygon' && currentPolygon.length > 0) {
      setTempPoint({ x, y });
    } else if (drawMode === 'circle' && currentCircle) {
      const dx = x - currentCircle.center.x;
      const dy = y - currentCircle.center.y;
      const radius = Math.sqrt(dx * dx + dy * dy);
      setCurrentCircle({ ...currentCircle, radius });
    } else if (drawMode === 'line' && currentLine) {
      setTempPoint({ x, y });
    }
  };

  const isNearStartPoint = (start, end) => {
    const threshold = 15;
    const dx = start.x - end.x;
    const dy = start.y - end.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  };

  const handleUndo = () => {
    if (currentPolygon.length > 0 || currentCircle || currentLine) {
      setCurrentPolygon([]);
      setCurrentCircle(null);
      setCurrentLine(null);
      setTempPoint(null);
      setIsDrawing(false);
    } else if (currentImage && currentImageShapes.length > 0) {
      setAllShapes(prev => ({
        ...prev,
        [currentImage._id]: currentImageShapes.slice(0, -1)
      }));
    }
  };

  const handleClearCurrent = () => {
    if (!currentImage) return;
    
    setAllShapes(prev => ({
      ...prev,
      [currentImage._id]: []
    }));
    setCurrentPolygon([]);
    setCurrentCircle(null);
    setCurrentLine(null);
    setTempPoint(null);
    setIsDrawing(false);
  };

  const saveAllShapes = async () => {
    setLoading(true);
    try {
      const formattedData = {
        username,
        code: roiCode,
        roiData: images.map(img => ({
          imageId: img._id,
          imageUrl: img.url,
          shapes: allShapes[img._id] || []
        }))
      };

      const response = await axios.post('/save-roi', formattedData);      
      message.success('All ROI shapes saved successfully!');
      
      if (onSaveClick) onSaveClick();
    } catch (err) {
      console.error('Save error:', err);
      message.error(err.response?.data?.msg || 'Failed to save shapes');
    } finally {
      setLoading(false);
    }
  };

  const getTotalShapeCount = () => {
    return Object.values(allShapes).reduce((sum, shapes) => sum + shapes.length, 0);
  };

  const getCompletedImageCount = () => {
    return Object.values(allShapes).filter(shapes => shapes.length > 0).length;
  };

  const resetDrawingState = () => {
    setCurrentPolygon([]);
    setCurrentCircle(null);
    setCurrentLine(null);
    setTempPoint(null);
    setIsDrawing(false);
  };

  const handleDrawModeChange = (mode) => {
    setDrawMode(mode);
    resetDrawingState();
  };

  if (loadingImages) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping"></div>
            <div className="relative w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-lg font-medium text-gray-700">Loading images...</p>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <Card className="text-center p-8 shadow-xl">
          <CloseCircleOutlined className="text-6xl text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No images found</h3>
          <p className="text-gray-500 mb-6">Please upload images to start drawing ROI</p>
          <Button type="primary" size="large" onClick={fetchImages}>Retry</Button>
        </Card>
      </div>
    );
  }

  const getDrawingInstructions = () => {
    if (drawMode === 'polygon') {
      if (currentPolygon.length === 0) return 'Click to start drawing polygon';
      if (currentPolygon.length < 3) return `${currentPolygon.length} point(s) • Keep clicking to draw`;
      return 'Click near start point to close polygon';
    } else if (drawMode === 'circle') {
      return currentCircle ? 'Click to set radius' : 'Click to set center point';
    } else if (drawMode === 'line') {
      return currentLine ? 'Click to set end point' : 'Click to set start point';
    }
  };

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                ROI Designer
              </h1>
              <p className="text-gray-600">
                Draw regions of interest on your images • {getCompletedImageCount()} of {images.length} completed
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl px-6 py-4 shadow-lg">
                <div className="text-3xl font-bold">{getTotalShapeCount()}</div>
                <div className="text-sm opacity-90">Total ROIs</div>
              </div>
              
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                onClick={saveAllShapes}
                loading={loading}
                disabled={getTotalShapeCount() === 0}
                className="h-auto py-4 px-8 text-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-lg hover:shadow-xl transition-all"
              >
                Save All ROIs
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-4 mb-2">
              <span className="text-sm font-medium text-gray-600">Progress</span>
              <span className="text-sm font-semibold text-blue-600">
                {getCompletedImageCount()}/{images.length} Camera
              </span>
            </div>
            <Progress 
              percent={Math.round((getCompletedImageCount() / images.length) * 100)} 
              strokeColor={{
                '0%': '#3b82f6',
                '100%': '#8b5cf6',
              }}
              trailColor="#e0e7ff"
              strokeWidth={12}
              className="mb-0"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Image Thumbnails Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 sticky top-4">
              <h3 className="font-semibold text-gray-700 mb-4 text-lg">Images ({images.length})</h3>
              <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
                {images.map((img, index) => {
                  const shapeCount = allShapes[img._id]?.length || 0;
                  const isActive = index === currentImageIndex;
                  
                  return (
                    <div
                      key={img._id}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        resetDrawingState();
                      }}
                      className={`group cursor-pointer rounded-xl overflow-hidden transition-all duration-300 ${
                        isActive
                          ? 'ring-4 ring-blue-500 shadow-xl scale-105'
                          : 'hover:ring-2 hover:ring-blue-300 hover:shadow-lg'
                      }`}
                    >
                      <div className="relative">
                        <img 
                          src={img.url} 
                          alt={`Image ${index + 1}`}
                          className="w-full h-28 object-cover"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent ${
                          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        } transition-opacity`}>
                          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                            <span className="text-white font-semibold text-sm">
                              Image {index + 1}
                            </span>
                            {shapeCount > 0 && (
                              <Tag color="success" icon={<CheckCircleOutlined />} className="m-0">
                                {shapeCount} ROI{shapeCount > 1 ? 's' : ''}
                              </Tag>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Canvas Area */}
          <div className="col-span-12 lg:col-span-9">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              {/* Controls */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      Image {currentImageIndex + 1} of {images.length}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {currentImageShapes.length} ROI{currentImageShapes.length !== 1 ? 's' : ''} drawn
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Tooltip title="Undo last action">
                      <Button
                        icon={<UndoOutlined />}
                        onClick={handleUndo}
                        disabled={currentImageShapes.length === 0 && !currentPolygon.length && !currentCircle && !currentLine}
                        className="hover:border-blue-500 hover:text-blue-500"
                      >
                        Undo
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Clear all ROIs on this image">
                      <Button
                        icon={<DeleteOutlined />}
                        onClick={handleClearCurrent}
                        disabled={currentImageShapes.length === 0}
                        danger
                      >
                        Clear
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                {/* Drawing Mode Selector */}
                <div className="flex gap-2">
                  <Button
                    type={drawMode === 'polygon' ? 'primary' : 'default'}
                    icon={<BorderOutlined />}
                    onClick={() => handleDrawModeChange('polygon')}
                    size="large"
                  >
                    Polygon
                  </Button>
                  <Button
                    type={drawMode === 'circle' ? 'primary' : 'default'}
                    icon={<RadiusSettingOutlined />}
                    onClick={() => handleDrawModeChange('circle')}
                    size="large"
                  >
                    Circle
                  </Button>
                  <Button
                    type={drawMode === 'line' ? 'primary' : 'default'}
                    icon={<LineOutlined />}
                    onClick={() => handleDrawModeChange('line')}
                    size="large"
                  >
                    Line
                  </Button>
                </div>
              </div>

              {/* Canvas */}
              <div className="relative">
                <div
                  className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden shadow-2xl"
                  style={{ aspectRatio: '16/9' }}
                  onClick={handleCanvasClick}
                  onMouseMove={handleCanvasMove}
                >
                  <img
                    ref={imageRef}
                    src={currentImage?.url}
                    alt="ROI"
                    className="hidden"
                    onLoad={() => {
                      const img = imageRef.current;
                      const canvas = canvasRef.current;

                      canvas.width = img.naturalWidth;
                      canvas.height = img.naturalHeight;

                      drawShapes();
                    }}
                  />

                  <canvas
                    ref={canvasRef}
                    className="w-full h-full cursor-crosshair"
                  />
                  
                  {/* Cursor hint when drawing */}
                  {isDrawing && (
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                      <p className="text-sm font-medium text-gray-700">
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-2"></span>
                        {getDrawingInstructions()}
                      </p>
                    </div>
                  )}

                  {/* Draw mode indicator */}
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                    <p className="text-sm font-medium text-gray-700">
                      Mode: <span className="text-blue-600 font-bold capitalize">{drawMode}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Label ROI"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setShapeMeta({ roiName: '', roiType: 'object' });
          setNewShapeData(null);
        }}
        okButtonProps={{ disabled: !shapeMeta.roiName }}
      >
        <Form layout="vertical">
          <Form.Item label="ROI Name">
            <Input
              value={shapeMeta.roiName}
              onChange={e =>
                setShapeMeta(prev => ({ ...prev, roiName: e.target.value }))
              }
              placeholder="Example: Entry Gate / Shelf-1 / Counter Area"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModernROIDrawer;